import React from 'react';
import { 
    Warehouse, 
    ScrollText, 
    ShieldCheck, 
    Clock, 
    Heart, 
    Target, 
    Eye, 
    Home, 
    Sparkles,
    Check
} from 'lucide-react';
import { AboutMetaData } from '../layouts/MetaData/AboutMetaData';

const About = () => {
    const features = [
        {
            icon: <Sparkles className="w-6 h-6 text-red-600" />,
            title: 'Uncompromising Quality',
            text: 'We uphold the highest hygiene and quality control standards, ensuring excellence from farm to fork.'
        },
        {
            icon: <Home className="w-6 h-6 text-red-600" />,
            title: 'Farm-Fresh Sourcing',
            text: 'Our meats come directly from our own farms, guaranteeing superior taste, safety, and freshness.'
        },
        {
            icon: <ScrollText className="w-6 h-6 text-red-600" />,
            title: 'Customized Cuts',
            text: 'Get meat tailored to your exact needs, expertly prepared with precision and care.'
        },
        {
            icon: <ShieldCheck className="w-6 h-6 text-red-600" />,
            title: 'Transparency & Traceability',
            text: 'Enjoy complete peace of mind knowing exactly where your meat comes from, thanks to our ethical and responsible sourcing.'
        },
        {
            icon: <Clock className="w-6 h-6 text-red-600" />,
            title: 'Convenience at Your Fingertips',
            text: 'Shop your way! Visit our store for a personalized experience or order online for seamless home delivery.'
        }
    ];

    return (
        <>
            <AboutMetaData />
            <div 
                className="pt-0"
                style={{
                    backgroundImage: `url(https://meatbuddy-assets.s3.eu-north-1.amazonaws.com/other-images/meatbuddy-image-web-banner-background-5.png)`,
                    backgroundRepeat: 'repeat',
                    backgroundSize: 'auto',
                    backgroundPosition: 'center'
                }}
            >
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
                    {/* Hero Section */}
                    <div className="text-center mb-10">
                        <h1 className="text-2xl md:text-3xl font-bold text-red-600 mb-3">
                            About Meatbuddy
                        </h1>
                        <div className="text-base text-gray-600 max-w-3xl mx-auto space-y-3">
                            <p className="text-justify">
                                At Meatbuddy, we are committed to providing premium, fresh, and high-quality meat products to consumers and the HORECA (Hotel, Restaurant, and Catering) sectors. Our mission is to elevate meat consumption standards through unmatched quality, freshness, and transparency in every cut. Sourced from our own farms and rigorously inspected, our products reflect our unwavering commitment to excellence.
                            </p>
                        </div>
                    </div>

                    {/* Mission & Vision Section */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-10">
                        <div className="bg-white rounded-lg p-6 shadow-md">
                            <div className="flex items-center mb-3">
                                <Target className="w-6 h-6 text-red-600 mr-3" />
                                <h2 className="text-lg font-semibold text-red-600">Our Mission</h2>
                            </div>
                            <p className="text-gray-600 text-sm leading-relaxed">
                                To deliver superior-quality, farm-fresh meat that is ethically sourced, hygienically and meticulously processed, and conveniently delivered—ensuring an exceptional, wholesome, and flavourful culinary experience for our customers.
                            </p>
                        </div>

                        <div className="bg-white rounded-lg p-6 shadow-md">
                            <div className="flex items-center mb-3">
                                <Eye className="w-6 h-6 text-red-600 mr-3" />
                                <h2 className="text-lg font-semibold text-red-600">Our Vision</h2>
                            </div>
                            <p className="text-gray-600 text-sm leading-relaxed">
                                To be the most trusted and preferred farm-to-fork meat brand, redefining industry standards through integrity, uncompromising quality, and customer-centric service.
                            </p>
                        </div>
                    </div>

                    {/* Main Content Grid */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-10">
                        {/* Farm-to-Fork Section */}
                        <div className="bg-white rounded-lg p-6 shadow-md">
                            <div className="flex items-center mb-3">
                                <Warehouse className="w-6 h-6 text-red-600 mr-3" />
                                <h2 className="text-lg font-semibold text-red-600">Farm-to-Fork Excellence</h2>
                            </div>
                            <p className="text-gray-600 text-sm leading-relaxed">
                                At Meatbuddy, we are more than just a meat shop—we are a farm-to-fork brand committed to quality at every step. Our meats come directly from our farms, where livestock is raised in a healthy, natural, and stress-free environment, ensuring superior taste, texture, and nutritional value.
                            </p>
                            <p className="text-gray-600 text-sm leading-relaxed mt-2">
                                With a rigorous in-house quality assurance process, we adhere to the highest hygiene and safety standards. Each batch undergoes multiple quality checks before reaching our customers, guaranteeing freshness, purity, and complete peace of mind with every purchase.
                            </p>
                        </div>

                        {/* Premium Cuts Section */}
                        <div className="bg-white rounded-lg p-6 shadow-md">
                            <div className="flex items-center mb-3">
                                <Heart className="w-6 h-6 text-red-600 mr-3" />
                                <h2 className="text-lg font-semibold text-red-600">Premium Cuts, Customized for You</h2>
                            </div>
                            <p className="text-gray-600 text-sm leading-relaxed">
                                At Meatbuddy, we understand that every customer has unique preferences. That's why we specialize in premium cuts and customized portioning. Whether you're a home cook seeking precise cuts or a professional chef requiring specialized portions, our expert butchers ensure every order is crafted with precision, care, and attention to detail.
                            </p>
                        </div>

                        {/* Transparency Section */}
                        <div className="bg-white rounded-lg p-6 shadow-md">
                            <div className="flex items-center mb-3">
                                <ShieldCheck className="w-6 h-6 text-red-600 mr-3" />
                                <h2 className="text-lg font-semibold text-red-600">Transparency and Ethical Sourcing</h2>
                            </div>
                            <p className="text-gray-600 text-sm leading-relaxed">
                                At Meatbuddy, trust is at the heart of everything we do. Our commitment to ethical sourcing ensures that we prioritize sustainable and humane farming practices. We maintain complete transparency, so you always know where your meat comes from. Every product is fully traceable, giving you confidence in its freshness, authenticity, and quality.
                            </p>
                        </div>

                        {/* Convenience Section */}
                        <div className="bg-white rounded-lg p-6 shadow-md">
                            <div className="flex items-center mb-3">
                                <Clock className="w-6 h-6 text-red-600 mr-3" />
                                <h2 className="text-lg font-semibold text-red-600">Seamless Shopping Experience</h2>
                            </div>
                            <p className="text-gray-600 text-sm leading-relaxed">
                                For your convenience, Meatbuddy brings you the best of both worlds—shop the way you prefer! Visit our store for a hands-on, personalized experience where our experts assist you in choosing the perfect cuts. Prefer to stay in? Simply order online, and we'll deliver fresh, high-quality meat straight to your doorstep. With our reliable logistics, you can trust that every order arrives fresh, retaining its natural flavors and nutritional goodness.
                            </p>
                        </div>
                    </div>

                    {/* Features Section */}
                    <div className="bg-white rounded-lg p-6 shadow-md mb-10">
                        <h2 className="text-xl font-semibold text-red-600 mb-6 text-center">
                            Why Choose Meatbuddy?
                        </h2>
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                            {features.map((feature, index) => (
                                <div key={index} className="flex items-start space-x-3 p-3 rounded-lg hover:bg-gray-50 transition-colors">
                                    <div className="flex-shrink-0">{feature.icon}</div>
                                    <div>
                                        <h3 className="font-semibold text-gray-900 mb-1 text-sm">{feature.title}</h3>
                                        <p className="text-gray-600 text-xs">{feature.text}</p>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>

                    {/* Call to Action */}
                    <div className="text-center">
                        <p className="text-base text-gray-600 mb-5">
                            At Meatbuddy, we're more than just a meat supplier—we're your trusted partner in crafting wholesome, flavourful meals. Experience the difference with our farm-fresh, expertly curated meats and elevate your culinary journey with confidence and ease.
                        </p>
                        <a 
                            href="/shop" 
                            className="inline-block bg-red-600 text-white px-6 py-2 rounded-lg font-medium hover:bg-red-700 transition-colors text-sm"
                        >
                            Shop Now
                        </a>
                    </div>
                </div>
            </div>
        </>
    );
};

export default About;